import { Card, Grid, Icon, IconButton, Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select, InputLabel } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useEffect, useState, useRef, useCallback } from "react";
import { get, postNoResponse, baseURL } from "utils/WebService";
import MDSnackbar from "components/MDSnackbar";
import routes from "routes";
import BaseDeDadosCreateEdit from "../baseDeDados/BaseDeDadosCreateEdit";

export default function FilaSolicitacoesConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { userFunctionalities } = useUser();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Auto-refresh timer
    const [refreshInterval, setRefreshInterval] = useState(30); // 30s padrão
    const refreshTimer = useRef(null);
    
    // Feedback Snackbar
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
    });
    const dummyFetchData = useCallback(() => {}, []);

    // Modal de Rejeição
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectingId, setRejectingId] = useState(null);
    const [motivoRejeicao, setMotivoRejeicao] = useState("");
    const [loadingAction, setLoadingAction] = useState(false);

    // Modal de Detalhes da Solicitação
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedSolicitacao, setSelectedSolicitacao] = useState(null);

    // Modal de Visualização Completa da Ficha do Devedor
    const [fichaDialogOpen, setFichaDialogOpen] = useState(false);
    const [selectedDevedorId, setSelectedDevedorId] = useState(null);

    // Modal de Aprovação de Migração (Suporte)
    const [approveMigracaoDialogOpen, setApproveMigracaoDialogOpen] = useState(false);
    const [approvingSolicitacao, setApprovingSolicitacao] = useState(null);
    const [carteirasOptions, setCarteirasOptions] = useState([]);
    const [usuariosOptions, setUsuariosOptions] = useState([]);
    const [statusFichaOptions, setStatusFichaOptions] = useState([]);
    const [approveCarteiraId, setApproveCarteiraId] = useState("");
    const [approveUsuarioId, setApproveUsuarioId] = useState("");
    const [approveStatusFichaId, setApproveStatusFichaId] = useState("");

    const loadData = () => {
        setLoading(true);
        get('/solicitacoes-suporte/pendentes').then(response => {
            setData(response?.list || response?.data || (Array.isArray(response) ? response : []));
            setLoading(false);
        }).catch(err => {
            console.error(err);
            setSbMessage(err.message || "Erro ao buscar solicitações");
            setErrorSB(true);
            setLoading(false);
        });
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (refreshTimer.current) {
            clearInterval(refreshTimer.current);
        }
        if (refreshInterval > 0) {
            refreshTimer.current = setInterval(() => {
                loadData();
            }, refreshInterval * 1000);
        }
        return () => {
            if (refreshTimer.current) {
                clearInterval(refreshTimer.current);
            }
        };
    }, [refreshInterval]);

    useEffect(() => {
        if (!approveCarteiraId) {
            setUsuariosOptions([]);
            return;
        }
        get(`/users/list-all-operators?carteiraId=${approveCarteiraId}`).then(res => {
            if (res && res.list) {
                setUsuariosOptions(res.list);
            } else if (Array.isArray(res)) {
                setUsuariosOptions(res);
            } else {
                setUsuariosOptions([]);
            }
        }).catch(() => setUsuariosOptions([]));
    }, [approveCarteiraId]);

    const openApproveMigracaoModal = (solicitacao) => {
        setApprovingSolicitacao(solicitacao);
        setApproveCarteiraId("");
        setApproveUsuarioId(""); // "" = Manter Responsável Atual
        setApproveStatusFichaId(""); // "" = Manter Status Atual
        setLoadingAction(true);

        Promise.all([
            get("/carteira/list-all").catch(() => null),
            get("/status-ficha/list-all").catch(() => null)
        ]).then(([cartRes, statusRes]) => {
            const carteirasRaw = cartRes?.list || cartRes?.data || (Array.isArray(cartRes) ? cartRes : []);
            const carteiras = carteirasRaw.filter(c => {
                const nome = (c.nome || c.ccNome || "").toLowerCase();
                return !nome.includes("processo");
            });
            const statusList = statusRes?.list || statusRes?.data || (Array.isArray(statusRes) ? statusRes : []);
            setCarteirasOptions(carteiras);
            setStatusFichaOptions(statusList);
            
            // Tenta pré-selecionar a carteira solicitada no dropdown
            if (solicitacao.nomeCarteiraDestino) {
                const found = carteiras.find(c => (c.nome || c.ccNome) === solicitacao.nomeCarteiraDestino);
                if (found) setApproveCarteiraId(found.id || found.cvId);
            }
            setApproveMigracaoDialogOpen(true);
        }).finally(() => setLoadingAction(false));
    };

    const handleAprovar = async (solicitacao) => {
        if (!solicitacao) return;
        const tipo = solicitacao.tipoSolicitacao;
        if (tipo === 'MIGRACAO_CARTEIRA') {
            openApproveMigracaoModal(solicitacao);
            return;
        }

        setLoadingAction(true);
        try {
            await postNoResponse(`/solicitacoes-suporte/${solicitacao.id}/aprovar`, {});
            setSbMessage("Solicitação aprovada com sucesso! A devolução foi realizada.");
            setSuccessSB(true);
            setViewDialogOpen(false);
            loadData();
        } catch (err) {
            setSbMessage(err.response?.data?.message || "Erro ao aprovar solicitação");
            setErrorSB(true);
        } finally {
            setLoadingAction(false);
        }
    };

    const confirmApproveMigracao = async () => {
        if (!approveCarteiraId) {
            setSbMessage("Selecione uma carteira de destino.");
            setErrorSB(true);
            return;
        }
        setLoadingAction(true);
        try {
            await postNoResponse(`/solicitacoes-suporte/${approvingSolicitacao.id}/aprovar`, {
                carteiraId: approveCarteiraId,
                usuarioId: approveUsuarioId || null,
                statusFichaId: approveStatusFichaId || null
            });
            setSbMessage("Migração de carteira aprovada e executada com sucesso!");
            setSuccessSB(true);
            setApproveMigracaoDialogOpen(false);
            setViewDialogOpen(false);
            loadData();
        } catch (err) {
            setSbMessage(err.response?.data?.message || err.message || "Erro ao aprovar migração");
            setErrorSB(true);
        } finally {
            setLoadingAction(false);
        }
    };

    const handleRejectClick = (id) => {
        setRejectingId(id);
        setMotivoRejeicao("");
        setRejectDialogOpen(true);
    };

    const confirmReject = async () => {
        if (!motivoRejeicao.trim()) {
            setSbMessage("Por favor, informe o motivo da rejeição.");
            setErrorSB(true);
            return;
        }
        setLoadingAction(true);
        try {
            await postNoResponse(`/solicitacoes-suporte/${rejectingId}/rejeitar`, { motivo: motivoRejeicao });
            setSbMessage("Solicitação rejeitada.");
            setSuccessSB(true);
            setRejectDialogOpen(false);
            setViewDialogOpen(false);
            loadData();
        } catch (err) {
            setSbMessage("Erro ao rejeitar solicitação");
            setErrorSB(true);
        } finally {
            setLoadingAction(false);
        }
    };

    const openViewModal = (solicitacao) => {
        setSelectedSolicitacao(solicitacao);
        setViewDialogOpen(true);
    };

    const columns = [
        {
            header: "Data Solicitação", accessorKey: "dataSolicitacao", cell: info => (
                <DefaultCell>{info.getValue()}</DefaultCell>
            )
        },
        {
            header: "Tipo", accessorKey: "tipoSolicitacao", cell: info => (
                <DefaultCell>
                    {info.getValue() === 'DEVOLUCAO_FICHA' 
                        ? 'Devolução (Ficha)' 
                        : info.getValue() === 'DEVOLUCAO_CONTRATO' 
                            ? 'Devolução (Contrato)' 
                            : info.getValue() === 'MIGRACAO_CARTEIRA' 
                                ? 'Migração de Carteira' 
                                : info.getValue()
                    }
                </DefaultCell>
            )
        },
        {
            header: "Devedor", accessorKey: "nomeDevedor", cell: info => (
                <DefaultCell>{info.row.original.nomeDevedor ? `${info.row.original.nomeDevedor} (${info.row.original.cpfCnpjDevedor})` : "-"}</DefaultCell>
            )
        },
        {
            header: "Contrato", accessorKey: "tituloContrato", cell: info => (
                <DefaultCell>{info.getValue() || "-"}</DefaultCell>
            )
        },
        {
            header: "Solicitante", accessorKey: "nomeSolicitante", cell: info => (
                <DefaultCell>{info.getValue()}</DefaultCell>
            )
        },
        {
            header: "Status", accessorKey: "status", cell: info => (
                <MDBox display="flex" alignItems="center">
                    <MDBox width="8px" height="8px" borderRadius="50%" bgColor={info.getValue() === 'PENDENTE' ? 'warning' : 'info'} mr={1} />
                    <MDTypography variant="button" fontWeight="medium" color="text">
                        {info.getValue()}
                    </MDTypography>
                </MDBox>
            )
        },
        {
            header: "Ações", accessorKey: "acoes", disableSortBy: true, cell: info => (
                <MDBox display="flex">
                    <Tooltip title="Analisar Solicitação">
                        <IconButton onClick={() => openViewModal(info.row.original)}>
                            <MDTypography variant="h5">
                                <Icon color="info">search</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                    {userFunctionalities?.includes("c067_U") && (
                        <>
                            <Tooltip title={info.row.original.tipoSolicitacao === 'MIGRACAO_CARTEIRA' ? "Aprovar Migração" : "Aprovar e Devolver"}>
                                <IconButton onClick={() => handleAprovar(info.row.original)}>
                                    <MDTypography variant="h5">
                                        <Icon color="success">check_circle</Icon>
                                    </MDTypography>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Rejeitar Solicitação">
                                <IconButton onClick={() => handleRejectClick(info.row.original.id)}>
                                    <MDTypography variant="h5">
                                        <Icon color="error">cancel</Icon>
                                    </MDTypography>
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </MDBox>
            )
        }
    ];

    return (
        <MDBox pb={3}>
            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content={sbMessage}
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content={sbMessage}
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite
            />

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
                            <MDTypography variant="h5">Fila de Solicitações</MDTypography>
                            
                            <MDBox display="flex" alignItems="center" gap={2}>
                                <MDBox width="160px" display="flex" alignItems="center" gap={1}>
                                    <InputLabel sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>Atualizar a cada:</InputLabel>
                                    <Select
                                        value={refreshInterval}
                                        onChange={(e) => setRefreshInterval(e.target.value)}
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                            height: "38px",
                                            background: darkMode ? "#4B4F5C" : "#fff",
                                            color: darkMode ? "#fff" : "#000"
                                        }}
                                    >
                                        <MenuItem value={0}>Desativado</MenuItem>
                                        <MenuItem value={15}>15s</MenuItem>
                                        <MenuItem value={30}>30s</MenuItem>
                                        <MenuItem value={60}>1 min</MenuItem>
                                        <MenuItem value={120}>2 min</MenuItem>
                                    </Select>
                                </MDBox>

                                <MDButton variant="gradient" color="info" onClick={loadData} disabled={loading}>
                                    <Icon>{loading ? "sync" : "refresh"}</Icon>
                                    &nbsp;Atualizar
                                </MDButton>
                            </MDBox>
                        </MDBox>
                        
                        {loading ? (
                            <MDBox display="flex" justifyContent="center" alignItems="center" p={5}>
                                <CircularProgress color="info" />
                            </MDBox>
                        ) : (
                            <DataTable
                                loading={loading}
                                columnData={columns}
                                data={data}
                                fetchData={dummyFetchData}
                                totalPages={1}
                                totalRecords={data.length}
                                tableState={tableState}
                                showTotalEntries={false}
                                noEndBorder
                            />
                        )}
                    </Card>
                </Grid>
            </Grid>

            {/* Modal de Detalhes da Solicitação */}
            <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Detalhes da Solicitação</DialogTitle>
                <DialogContent dividers>
                    {selectedSolicitacao && (
                        <MDBox display="flex" flexDirection="column" gap={2}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold">Devedor:</MDTypography>
                                <MDTypography variant="button" display="block">{selectedSolicitacao.nomeDevedor} ({selectedSolicitacao.cpfCnpjDevedor})</MDTypography>
                            </MDBox>
                            
                            {selectedSolicitacao.tipoSolicitacao === 'DEVOLUCAO_CONTRATO' && (
                                <MDBox>
                                    <MDTypography variant="button" fontWeight="bold">Contrato:</MDTypography>
                                    <MDTypography variant="button" display="block">{selectedSolicitacao.tituloContrato}</MDTypography>
                                </MDBox>
                            )}

                            {selectedSolicitacao.tipoSolicitacao === 'MIGRACAO_CARTEIRA' && (
                                <MDBox>
                                    <MDTypography variant="button" fontWeight="bold">Carteira de Destino Solicitada:</MDTypography>
                                    <MDTypography variant="button" display="block" color="info" fontWeight="bold">
                                        {selectedSolicitacao.nomeCarteiraDestino || "Não especificada"}
                                    </MDTypography>
                                </MDBox>
                            )}

                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold">Observação da Solicitação:</MDTypography>
                                <MDBox mt={1} p={2} borderRadius="md" bgColor={darkMode ? "dark" : "grey-100"}>
                                    <MDTypography variant="button" style={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedSolicitacao.observacao || "Nenhuma observação informada."}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            {selectedSolicitacao.nomeArquivo && (
                                <MDBox>
                                    <MDTypography variant="button" fontWeight="bold">Documento / Comprovante Anexado:</MDTypography>
                                    <MDBox mt={1}>
                                        <MDButton
                                            variant="outlined"
                                            color="info"
                                            size="small"
                                            startIcon={<Icon>attach_file</Icon>}
                                            onClick={() => {
                                                const url = `${baseURL}/solicitacoes-suporte/${selectedSolicitacao.id}/download-anexo`;
                                                window.open(url, '_blank');
                                            }}
                                        >
                                            Baixar {selectedSolicitacao.nomeArquivo}
                                        </MDButton>
                                    </MDBox>
                                </MDBox>
                            )}

                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold">Histórico e Ficha:</MDTypography>
                                <MDTypography variant="caption" display="block" color="text">
                                    Para visualizar os anexos da ficha ou histórico detalhado, acesse a ficha do devedor pela Base de Dados.
                                </MDTypography>
                                <MDButton 
                                    variant="outlined" 
                                    color="info" 
                                    size="small" 
                                    sx={{ mt: 1 }}
                                    onClick={() => {
                                        if (selectedSolicitacao?.idDevedor) {
                                            setSelectedDevedorId(selectedSolicitacao.idDevedor);
                                            setFichaDialogOpen(true);
                                        }
                                    }}
                                >
                                    Abrir Ficha do Devedor
                                </MDButton>
                            </MDBox>
                        </MDBox>
                    )}
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setViewDialogOpen(false)} color="dark">Fechar</MDButton>
                    {userFunctionalities?.includes("c067_U") && (
                        <>
                            <MDButton 
                                onClick={() => handleRejectClick(selectedSolicitacao?.id)} 
                                color="error" 
                                variant="gradient"
                                disabled={loadingAction}
                            >
                                Rejeitar
                            </MDButton>
                            <MDButton 
                                onClick={() => handleAprovar(selectedSolicitacao)} 
                                color="success" 
                                variant="gradient"
                                disabled={loadingAction}
                            >
                                {loadingAction ? <CircularProgress size={24} color="inherit" /> : (selectedSolicitacao?.tipoSolicitacao === 'MIGRACAO_CARTEIRA' ? "Aprovar Migração" : "Aprovar Devolução")}
                            </MDButton>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Modal de Aprovação de Migração pelo Suporte */}
            <Dialog open={approveMigracaoDialogOpen} onClose={() => setApproveMigracaoDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Aprovar Migração de Carteira</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" color="text" mb={2}>
                        Confirme a carteira de destino e, se necessário, associe um novo responsável e status para a ficha (ou mantenha os atuais):
                    </MDTypography>
                    <MDInput
                        select
                        fullWidth
                        label="Carteira de Destino"
                        value={approveCarteiraId}
                        onChange={(e) => setApproveCarteiraId(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 2 }}
                    >
                        <MenuItem value="">Selecione...</MenuItem>
                        {carteirasOptions.map((c, i) => (
                            <MenuItem key={i} value={c.id || c.cvId}>{c.nome || c.ccNome}</MenuItem>
                        ))}
                    </MDInput>

                    <MDInput
                        select
                        fullWidth
                        label="Novo Responsável / Proprietário"
                        value={approveUsuarioId}
                        onChange={(e) => setApproveUsuarioId(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 2 }}
                    >
                        <MenuItem value="">Manter Responsável Atual</MenuItem>
                        {usuariosOptions.map((c, i) => (
                            <MenuItem key={i} value={c.id || c.cvId}>{c.nome || c.ccNome}</MenuItem>
                        ))}
                    </MDInput>

                    <MDInput
                        select
                        fullWidth
                        label="Novo Status da Ficha"
                        value={approveStatusFichaId}
                        onChange={(e) => setApproveStatusFichaId(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    >
                        <MenuItem value="">Manter Status Atual</MenuItem>
                        {statusFichaOptions.map((c, i) => (
                            <MenuItem key={i} value={c.id || c.cvId}>{c.nome || c.ccNome}</MenuItem>
                        ))}
                    </MDInput>
                </DialogContent>
                <DialogActions>
                    <MDButton color="secondary" onClick={() => setApproveMigracaoDialogOpen(false)}>Cancelar</MDButton>
                    <MDButton color="success" variant="gradient" onClick={confirmApproveMigracao} disabled={loadingAction}>
                        {loadingAction ? <CircularProgress size={24} color="inherit" /> : "Confirmar e Aprovar"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Modal de Rejeição */}
            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Rejeitar Solicitação</DialogTitle>
                <DialogContent>
                    <MDBox pt={1}>
                        <MDTypography variant="button" color="text">
                            Informe o motivo da recusa para o operador:
                        </MDTypography>
                        <MDBox mt={2}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                value={motivoRejeicao}
                                onChange={(e) => setMotivoRejeicao(e.target.value)}
                                placeholder="Motivo da rejeição..."
                            />
                        </MDBox>
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setRejectDialogOpen(false)} color="dark">
                        Cancelar
                    </MDButton>
                    <MDButton onClick={confirmReject} color="error" variant="gradient" disabled={loadingAction}>
                        {loadingAction ? <CircularProgress size={24} color="inherit" /> : "Confirmar Rejeição"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Modal de Visualização da Ficha do Devedor */}
            <Dialog
                open={fichaDialogOpen}
                onClose={() => setFichaDialogOpen(false)}
                fullWidth
                maxWidth="xl"
                PaperProps={{
                    style: {
                        backgroundColor: darkMode ? "#4B4F5C" : "#fafafa",
                        minHeight: "80vh"
                    }
                }}
            >
                <DialogContent sx={{ p: 1 }}>
                    {selectedDevedorId && (
                        <BaseDeDadosCreateEdit
                            recordId={selectedDevedorId}
                            closeCreateEdit={() => setFichaDialogOpen(false)}
                            isViewOnly={true}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </MDBox>
    );
}
